import { useEffect, useMemo } from 'react';
import {
  ArrowRight,
  BookOpen,
  ChevronRight,
  ExternalLink,
  Globe2,
  Images,
  Mail,
  Megaphone,
  Menu,
  Palette,
  Radio,
  Video,
  WalletCards,
  X
} from 'lucide-react';
import { useLanguage } from './i18n/LanguageProvider';
import {
  contact,
  flywheel,
  groupUnits,
  languageLabels,
  localized,
  resolveSiteKey,
  sites
} from './data/sites';
import type { Feature, SiteContent } from './data/sites';
import { useState } from 'react';

const siteLinks = [
  { key: 'group', label: 'MINI MALL GROUP', href: 'https://mmallgroup.com/' },
  { key: 'enring', label: 'Enring', href: 'https://enring.co.nz/' },
  { key: 'minimall', label: 'MINI MALL', href: 'https://mmall.co.nz/' },
  { key: 'sentribee', label: 'Sentribee AI', href: 'https://sentribee.ai/', external: true }
];

function setMeta(name: string, content: string, attribute = 'name') {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.content = content;
}

function App() {
  const { language, setLanguage } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const site = useMemo(() => {
    return sites[resolveSiteKey(window.location.hostname, window.location.search)];
  }, []);

  useEffect(() => {
    const title = localized(site.metaTitle, language);
    const description = localized(site.metaDescription, language);

    document.title = title;
    setMeta('description', description);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:url', `https://${site.host}/`, 'property');
    setMeta('og:image', 'https://mmallgroup.com/assets/commerce-intelligence-hero.png', 'property');
    setMeta('twitter:card', 'summary_large_image');
  }, [language, site]);

  const copy = {
    navContact: language === 'zh' ? '联系' : 'Contact',
    overview: language === 'zh' ? '集团概览' : 'Group Overview',
    units: language === 'zh' ? '业务单元' : 'Business Units',
    flywheel: language === 'zh' ? '增长飞轮' : 'Growth Flywheel',
    highlights: language === 'zh' ? '投资亮点' : 'Investor Highlights',
    vision: language === 'zh' ? '未来愿景' : 'Future Vision',
    focus: language === 'zh' ? '业务重点' : 'Strategic Focus',
    visit: language === 'zh' ? '访问站点' : 'Visit site',
    groupHome: language === 'zh' ? '返回集团' : 'Group home',
    language: language === 'zh' ? '语言' : 'Language',
    builtIn: language === 'zh' ? '集团业务体系' : 'Group platform',
    learn: language === 'zh' ? '查看业务' : 'View business',
    contact: language === 'zh' ? '联系团队' : 'Contact team'
  };

  if (site.key === 'enring') {
    return <DeepEnringSite site={site} language={language} setLanguage={setLanguage} copy={copy} />;
  }

  if (site.key === 'minimall') {
    return <DeepMiniMallSite site={site} language={language} setLanguage={setLanguage} copy={copy} />;
  }

  return (
    <div className="min-h-screen bg-porcelain text-ink">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-ink/[0.82] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a className="flex items-center gap-3 text-white" href="https://mmallgroup.com/" aria-label="MINI MALL GROUP">
            <span className="grid h-9 w-9 place-items-center rounded-lg border border-white/[0.15] bg-white text-sm font-black text-ink">
              MM
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold leading-tight">{site.name}</span>
              <span className="block text-[11px] font-medium leading-tight text-white/60 sm:text-xs">
                {language === 'zh' ? '商业 · 社群 · 运营智能' : 'Commerce · Community · Intelligence'}
              </span>
            </span>
          </a>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main navigation">
            {siteLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noreferrer' : undefined}
                className={`rounded-lg px-3 py-2 text-sm transition ${
                  link.key === site.key ? 'bg-white text-ink' : 'text-white/75 hover:bg-white/10 hover:text-white'
                }`}
              >
                <span className="inline-flex items-center gap-1">
                  {link.label}
                  {link.external && <ExternalLink className="h-3.5 w-3.5" />}
                </span>
              </a>
            ))}
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <LanguageSwitch language={language} setLanguage={setLanguage} label={copy.language} />
            <a
              href="#contact"
              className="inline-flex items-center gap-2 rounded-lg bg-signal px-4 py-2 text-sm font-semibold text-ink transition hover:bg-white"
            >
              <Mail className="h-4 w-4" />
              {copy.navContact}
            </a>
          </div>

          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-lg border border-white/[0.15] text-white lg:hidden"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Toggle navigation"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-white/10 bg-ink px-4 py-4 lg:hidden">
            <nav className="mx-auto grid max-w-7xl gap-2" aria-label="Mobile navigation">
              {siteLinks.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noreferrer' : undefined}
                  className="flex items-center justify-between rounded-lg px-3 py-3 text-sm text-white/80 hover:bg-white/10"
                >
                  {link.label}
                  {link.external ? <ExternalLink className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </a>
              ))}
              <div className="mt-2">
                <LanguageSwitch language={language} setLanguage={setLanguage} label={copy.language} />
              </div>
            </nav>
          </div>
        )}
      </header>

      <main>
        <Hero site={site} language={language} secondaryLabel={site.secondaryCta ? localized(site.secondaryCta, language) : copy.contact} />

        {site.key === 'group' ? (
          <GroupHome site={site} language={language} copy={copy} />
        ) : (
          <BusinessHome site={site} language={language} copy={copy} />
        )}
      </main>

      <Footer language={language} />
    </div>
  );
}

function getBusinessPage(siteKey: 'enring' | 'minimall') {
  const params = new URLSearchParams(window.location.search);
  const pageParam = params.get('page');
  if (pageParam) return pageParam;

  const page = window.location.pathname.replace(/^\/+/, '').split('/')[0];
  return page || 'home';
}

function businessHref(siteKey: 'enring' | 'minimall', page: string) {
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  if (isLocal) {
    return `/?site=${siteKey}&page=${page}`;
  }
  return page === 'home' ? '/' : `/${page}`;
}

function MiniMallLogo() {
  return (
    <span className="minimall-brand-lockup">
      <span className="minimall-brand-mark" aria-hidden="true">
        <img src="/assets/minimall-logo.jpg" alt="" />
      </span>
      <span className="minimall-brand-text">
        <span className="minimall-brand-name">MINI MALL</span>
        <span className="minimall-brand-subtitle">Community Commerce Network</span>
      </span>
    </span>
  );
}

function EnringSite({
  site,
  language,
  setLanguage,
  copy
}: {
  site: SiteContent;
  language: 'en' | 'zh';
  setLanguage: (language: 'en' | 'zh') => void;
  copy: Record<string, string>;
}) {
  const pillars = [
    {
      title: 'Mobile Platform Service',
      body:
        language === 'zh'
          ? '为商户建立专属移动互联网渠道，沉淀客户资源，支持全球化服务。'
          : 'Establish exclusive mobile Internet channels for merchants and protect dedicated customer resources.'
    },
    {
      title: 'Connect The World',
      body:
        language === 'zh'
          ? '基于互联网系统，为中小企业提供交易与服务闭环解决方案。'
          : 'Provide trading and service solutions for SMEs through Internet systems and mobile devices.'
    },
    {
      title: 'At your fingertips',
      body:
        language === 'zh'
          ? '提升线上门店体验，让远距离消费者也能获得即时、沉浸式服务。'
          : 'Enrich online-store experiences and provide instant service for consumers miles away.'
    }
  ];

  const markets = ['China', 'Japan', 'Australia', 'New Zealand', 'Europe', 'Singapore', 'Canada'];

  return (
    <div className="enring-page min-h-screen bg-white text-[#6f6f6f]">
      <header className="enring-header">
        <a href="https://enring.co.nz/" aria-label="Enring" className="flex items-center gap-3">
          <img src="/assets/enring-logo.png" alt="Enring" className="h-8 w-auto" />
        </a>
        <nav className="hidden items-center gap-8 text-sm md:flex">
          {['Home', 'Products', 'Business Solution', 'Business Cloud', 'Service', 'About Us'].map((item) => (
            <a key={item} href="#focus" className="text-white transition hover:text-[#3a7cdd]">
              {item}
            </a>
          ))}
        </nav>
        <PlainLanguageSwitch language={language} setLanguage={setLanguage} label={copy.language} mode="light" />
      </header>

      <main>
        <section className="enring-hero">
          <div className="mx-auto flex min-h-[760px] max-w-7xl flex-col justify-center px-4 pt-24 text-center text-white sm:px-6 lg:px-8">
            <h1 className="mx-auto max-w-5xl text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
              {language === 'zh' ? '连接企业与客户的一切触点' : 'Drive All Connections Between Businesses and Customers'}
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-white/85">
              {localized(site.lead, language)}
            </p>
            <div className="mt-20 grid gap-8 md:grid-cols-3">
              {pillars.map((pillar, index) => (
                <article key={pillar.title} className="text-center">
                  <div className="mx-auto grid h-16 w-16 place-items-center rounded-full border border-white/35 text-2xl font-semibold">
                    {index + 1}
                  </div>
                  <h2 className="mt-5 text-xl font-semibold text-white">{pillar.title}</h2>
                  <p className="mt-4 text-base leading-7 text-white/80">{pillar.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="focus" className="enring-section bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <EnringTitle
              blue={language === 'zh' ? '全球' : 'Market'}
              rest={language === 'zh' ? '市场连接' : 'Sites'}
              body={
                language === 'zh'
                  ? 'Enring 创立于新西兰，并持续关注全球市场，帮助商户走向全球，也让消费者购买全球。'
                  : 'ENRING was founded in New Zealand and focuses on the global market. We help merchants go global and allow customers to purchase globally.'
              }
            />
            <div className="mx-auto mt-12 grid max-w-4xl grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
              {markets.map((market, index) => (
                <div key={market} className="enring-market">
                  <span className={index < 4 ? 'bg-[#3a7cdd]' : 'bg-[#f5a623]'} />
                  {market}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="enring-section bg-[#f7f7f9]">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <EnringTitle
              blue="Smart"
              rest="Retail"
              body={
                language === 'zh'
                  ? '整合移动平台、商户服务、支付结算与数据工具，帮助零售、餐饮、酒店、品牌与服务行业构建线上业务闭环。'
                  : 'Integrated mobile platform, merchant service, payment settlement and data tools for retail, restaurant, hotel, brand and service businesses.'
              }
            />
            <FeatureGrid features={site.features} language={language} compact />
          </div>
        </section>

        <section className="enring-section bg-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-6 md:grid-cols-4">
              {[
                ['2014', language === 'zh' ? '公司成立' : 'Company founded'],
                ['FSP', language === 'zh' ? '新西兰金融服务能力' : 'New Zealand financial service capability'],
                ['20M+', language === 'zh' ? '多年交易量突破纽币' : 'NZD transaction volume milestone'],
                ['Global', language === 'zh' ? '服务跨境商业市场' : 'Cross-border commerce markets']
              ].map(([value, label]) => (
                <div key={value} className="rounded bg-[#f7f7f9] p-8 text-center">
                  <p className="text-4xl font-semibold text-[#3a7cdd]">{value}</p>
                  <p className="mt-3 text-base text-[#6f6f6f]">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Contact language={language} />
      </main>
    </div>
  );
}

function EnringTitle({ blue, rest, body }: { blue: string; rest: string; body: string }) {
  return (
    <div className="mx-auto max-w-3xl text-center">
      <h2 className="text-3xl font-semibold text-[#6f6f6f] sm:text-4xl">
        <span className="text-[#3a7cdd]">{blue}</span> {rest}
      </h2>
      <div className="mx-auto mt-5 h-0.5 w-20 bg-[#3a7cdd]" />
      <p className="mt-7 text-base leading-8 text-[#6f6f6f]">{body}</p>
    </div>
  );
}

function MiniMallSite({
  site,
  language,
  setLanguage,
  copy
}: {
  site: SiteContent;
  language: 'en' | 'zh';
  setLanguage: (language: 'en' | 'zh') => void;
  copy: Record<string, string>;
}) {
  const solutions = [
    {
      title: language === 'zh' ? '保税仓服务' : 'Bonded Warehouse Service',
      body:
        language === 'zh'
          ? '品牌无需理解复杂的中国保税仓制度，货物到指定仓库后，消费者或代理商下单才产生关税，支持 2 小时自动清关、1 小时发货。'
          : 'Brands do not need to manage complex China bonded-warehouse rules. Duties are triggered only after consumer or agent orders, with automated clearance and rapid dispatch.'
    },
    {
      title: language === 'zh' ? '外币结算' : 'Foreign Currency Settlement',
      body:
        language === 'zh'
          ? '通过新西兰持牌直属支付子公司进行外币结算，降低品牌跨境运营成本。'
          : 'Settlement through a New Zealand licensed payment subsidiary helps reduce cross-border operating cost.'
    },
    {
      title: language === 'zh' ? '分级价格制度' : 'Tiered Pricing System',
      body:
        language === 'zh'
          ? '识别代理级别与消费者群体，采用差异化价格策略并实现差价秒级返现，确保终端价格稳定。'
          : 'Agent level and customer segment recognition enables differential pricing and instant cashback to keep retail prices stable.'
    }
  ];

  const revenue = [
    ['2%-3%', language === 'zh' ? '跨境支付服务费' : 'Cross-border payment fee'],
    ['3%-5%', language === 'zh' ? '平台订单服务分成' : 'Platform order service share'],
    ['20%+', language === 'zh' ? '平台活动预采购利润空间' : 'Campaign pre-purchase margin'],
    ['$50-100', language === 'zh' ? '保税仓基础服务费/平米/年' : 'Bonded warehouse fee per sqm/year']
  ];

  const milestones = [
    ['2015-2019', language === 'zh' ? '首批微信跨境支付服务商，海外公众号商城服务商。' : 'Early WeChat cross-border payment and overseas official-account commerce service provider.'],
    ['2021-2022', language === 'zh' ? '打造海外直播带货小程序，完成 5000+ 场直播。' : 'Built overseas livestream commerce mini-programs and supported 5,000+ sessions.'],
    ['2023', language === 'zh' ? '首个跨境社交团购平台，创业首年发展超过 1000 名团长。' : 'Cross-border social group-buying platform with 1,000+ community leaders in the first year.'],
    ['2024-2025', language === 'zh' ? '年交易规模超过 1800 万纽币，并获 NZTE 认可为黄金签证可直投企业。' : 'Annual transaction scale exceeded NZD 18M and NZTE accepted the group for direct investment eligibility.']
  ];

  return (
    <div className="minimall-page min-h-screen bg-white text-[#1e2022]">
      <header className="minimall-header">
        <a href="https://mmall.co.nz/" aria-label="MINI MALL" className="minimall-brand-link">
          <MiniMallLogo />
        </a>
        <nav className="hidden items-center gap-8 text-sm md:flex">
          {(language === 'zh' ? ['主页', '解决方案', '商业模式', '投资机会'] : ['Home', 'Solution', 'Business Model', 'Investment']).map((item) => (
            <a key={item} href="#solution" className="text-[#1e2022] transition hover:text-[#d4145a]">
              {item}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <PlainLanguageSwitch language={language} setLanguage={setLanguage} label={copy.language} mode="dark" />
          <a href="#contact" className="hidden rounded px-5 py-3 text-sm font-semibold text-white md:inline-flex minimall-cta">
            {copy.contact}
          </a>
        </div>
      </header>

      <main>
        <section className="minimall-hero">
          <div className="mx-auto grid min-h-[760px] max-w-7xl items-center gap-12 px-4 pt-28 sm:px-6 lg:grid-cols-[1fr_0.86fr] lg:px-8">
            <div className="minimall-photo">
              <img src="/assets/minimall-china-welcome-hero.png" alt="" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#d4145a]">
                {language === 'zh' ? '跨境社群商业网络' : 'Cross-Border Community Commerce Network'}
              </p>
              <h1 className="mt-5 text-5xl font-light leading-[1.12] tracking-normal sm:text-6xl">
                {language === 'zh' ? (
                  <>
                    让海外品牌
                    <br />
                    <span className="font-semibold text-[#d4145a]">进入中国私域商业网络</span>
                  </>
                ) : (
                  <>
                    Help overseas brands enter
                    <br />
                    <span className="font-semibold text-[#d4145a]">China’s private commerce network</span>
                  </>
                )}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-[#6d767d]">
                {language === 'zh'
                  ? 'MINI MALL 帮助海外品牌通过微信生态、代理网络、返现激励与跨境履约能力进入中国市场，建立可分销、可复购、可沉淀的增长体系。'
                  : 'MINI MALL helps overseas brands enter China through the WeChat ecosystem, agent networks, cashback incentives and cross-border fulfillment, building repeatable growth through distribution and repurchase.'}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#solution" className="rounded px-6 py-3 text-sm font-semibold text-white minimall-cta">
                  {language === 'zh' ? '了解业务模式' : 'Explore the Model'}
                </a>
                <a href="#investment" className="rounded border border-[#d4145a]/25 px-6 py-3 text-sm font-semibold text-[#d4145a]">
                  {language === 'zh' ? '品牌合作咨询' : 'Partner with MINI MALL'}
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="minimall-section bg-[#fafafa]" id="solution">
          <MiniMallTitle
            eyebrow={language === 'zh' ? 'MINI MALL 的使命' : 'Mission'}
            title={language === 'zh' ? '成为海外中小品牌进入中国的必选解决方案。' : 'The preferred China-entry solution for overseas SME brands.'}
            body={
              language === 'zh'
                ? '深耕微信生态，成为微信电商生态海外商品最大入口，并在五年内成长为高价值跨境私域电商平台。'
                : 'Deeply integrated with the WeChat ecosystem, MINI MALL aims to become a major overseas goods entrance for China’s private-domain commerce.'
            }
          />
          <div className="mx-auto mt-12 grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-3 lg:px-8">
            {solutions.map((item) => (
              <article key={item.title} className="minimall-card">
                <h3 className="text-2xl font-semibold">{item.title}</h3>
                <p className="mt-5 leading-8 text-[#6d767d]">{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="minimall-section bg-white">
          <MiniMallTitle
            eyebrow={language === 'zh' ? '商业模式' : 'Business Model'}
            title={language === 'zh' ? '多元化收入结构支持可持续成长。' : 'Multiple revenue lines designed for sustainable growth.'}
            body={language === 'zh' ? '跨境支付、平台服务、活动预采购与保税仓服务共同构成平台收入模型。' : 'Payment, platform service, campaign procurement and bonded warehouse services form the platform revenue model.'}
          />
          <div className="mx-auto mt-12 grid max-w-7xl gap-5 px-4 sm:px-6 md:grid-cols-4 lg:px-8">
            {revenue.map(([value, label]) => (
              <div key={label} className="rounded bg-[#fff4f7] p-7 text-center">
                <p className="text-4xl font-semibold text-[#d4145a]">{value}</p>
                <p className="mt-3 text-sm font-semibold text-[#6d767d]">{label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="minimall-section bg-[#f7f8fa]">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div>
              <MiniMallTitle
                align="left"
                eyebrow={language === 'zh' ? '核心优势' : 'Core Advantages'}
                title={language === 'zh' ? '微信跨境支付、双牌照金融能力与集团协同。' : 'WeChat cross-border payment, financial capability and group synergy.'}
                body={
                  language === 'zh'
                    ? 'Enring 支撑 MINI MALL GROUP 的人民币 ⇄ 外币结算系统，并支持保税仓、跨境零售、分销返现等核心业务。'
                    : 'Enring supports MINI MALL GROUP’s RMB and foreign currency settlement system across bonded warehouse, cross-border retail and distribution cashback.'
                }
              />
            </div>
            <div className="grid gap-4">
              {[
                language === 'zh' ? '新西兰最早微信跨境支付服务商之一，长期位居第一梯队。' : 'One of New Zealand’s early WeChat cross-border payment service providers.',
                language === 'zh' ? '多年年交易量突破 2,000 万纽币，覆盖零售、旅游、百货、跨境电商等场景。' : 'Annual transaction volume exceeded NZD 20M across retail, tourism, department store and cross-border commerce scenarios.',
                language === 'zh' ? '2025 年升级为支付 + 资金管理双能力金融机构。' : 'Upgraded in 2025 from payment company to payment plus capital-management capability.'
              ].map((item) => (
                <div key={item} className="rounded bg-white p-5 text-[#6d767d] shadow-[0_18px_50px_rgba(30,32,34,0.06)]">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="minimall-section bg-white">
          <MiniMallTitle
            eyebrow={language === 'zh' ? '团队发展里程碑' : 'Milestones'}
            title={language === 'zh' ? '从微信支付到跨境社交团购平台。' : 'From WeChat payment to cross-border social commerce.'}
            body={language === 'zh' ? 'MINI MALL 团队围绕微信生态、直播带货、社交团购和跨境电商持续演进。' : 'The team has evolved through WeChat commerce, livestream selling, social group buying and cross-border commerce.'}
          />
          <div className="mx-auto mt-12 max-w-5xl px-4 sm:px-6 lg:px-8">
            {milestones.map(([year, body]) => (
              <div key={year} className="minimall-timeline">
                <span>{year}</span>
                <p>{body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="minimall-section bg-[#d4145a] text-white" id="investment">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/75">AIP / NZTE</p>
              <h2 className="mt-4 text-4xl font-semibold leading-tight">
                {language === 'zh' ? '新西兰黄金签证可接受直接投资项目。' : 'Acceptable Direct Investment opportunity for New Zealand AIP.'}
              </h2>
              <p className="mt-5 leading-8 text-white/82">
                {language === 'zh'
                  ? '集团总公司已于 2025 年 8 月 22 日获得 NZTE Acceptable Direct Investments 认证。当前经专业机构评估整体估值约 NZD 3000 万。'
                  : 'The group received NZTE Acceptable Direct Investments approval on 22 August 2025. Professional assessment places the group valuation at approximately NZD 30M.'}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ['NZD 5M', language === 'zh' ? '股权或债转股投资满足 AIP 直投要求' : 'Equity or convertible investment for AIP direct investment'],
                ['27 days', language === 'zh' ? '三年累计居住要求极低' : 'Low residence requirement across three years'],
                ['Family', language === 'zh' ? '家庭成员一次性获得身份路径' : 'Family pathway included'],
                ['Clear', language === 'zh' ? '投资结构清晰，审批更快' : 'Clear structure and faster review']
              ].map(([value, label]) => (
                <div key={value} className="rounded bg-white/12 p-6">
                  <p className="text-3xl font-semibold">{value}</p>
                  <p className="mt-3 text-sm leading-6 text-white/78">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Contact language={language} />
        <MiniMallFooter language={language} />
      </main>
    </div>
  );
}

function MiniMallTitle({
  eyebrow,
  title,
  body,
  align = 'center'
}: {
  eyebrow: string;
  title: string;
  body: string;
  align?: 'center' | 'left';
}) {
  return (
    <div className={`mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 ${align === 'center' ? 'text-center' : 'text-left'}`}>
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#d4145a]">{eyebrow}</p>
      <h2 className="mt-4 text-3xl font-semibold leading-tight text-[#1e2022] sm:text-4xl">{title}</h2>
      <p className="mt-5 text-lg leading-8 text-[#6d767d]">{body}</p>
    </div>
  );
}

function LanguageSwitch({
  language,
  setLanguage,
  label
}: {
  language: 'en' | 'zh';
  setLanguage: (language: 'en' | 'zh') => void;
  label: string;
}) {
  return (
    <div className="inline-flex items-center gap-2 rounded-lg border border-white/[0.15] bg-white/[0.08] p-1 text-white">
      <Globe2 className="ml-2 h-4 w-4 text-white/60" aria-hidden="true" />
      <span className="sr-only">{label}</span>
      {(['en', 'zh'] as const).map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setLanguage(code)}
          className={`rounded-md px-3 py-1.5 text-xs font-semibold transition ${
            language === code ? 'bg-white text-ink' : 'text-white/70 hover:text-white'
          }`}
        >
          {languageLabels[code]}
        </button>
      ))}
    </div>
  );
}

function PlainLanguageSwitch({
  language,
  setLanguage,
  label,
  mode
}: {
  language: 'en' | 'zh';
  setLanguage: (language: 'en' | 'zh') => void;
  label: string;
  mode: 'light' | 'dark';
}) {
  const isLight = mode === 'light';
  return (
    <div
      className={`inline-flex items-center gap-1 rounded border p-1 ${
        isLight ? 'border-white/30 bg-white/10 text-white' : 'border-[#e5e4e4] bg-white text-[#1e2022]'
      }`}
    >
      <span className="sr-only">{label}</span>
      {(['en', 'zh'] as const).map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => setLanguage(code)}
          aria-label={languageLabels[code]}
          className={`rounded px-3 py-1.5 text-xs font-semibold transition ${
            language === code
              ? isLight
                ? 'bg-white text-[#1e2022]'
                : 'bg-[#d4145a] text-white'
              : isLight
                ? 'text-white/75 hover:text-white'
                : 'text-[#6d767d] hover:text-[#d4145a]'
          }`}
        >
          <span className="sm:hidden">{code === 'en' ? 'EN' : '中'}</span>
          <span className="hidden sm:inline">{languageLabels[code]}</span>
        </button>
      ))}
    </div>
  );
}

function Hero({
  site,
  language,
  secondaryLabel
}: {
  site: SiteContent;
  language: 'en' | 'zh';
  secondaryLabel: string;
}) {
  return (
    <section className="relative overflow-hidden bg-ink pt-16 text-white">
      <div className="absolute inset-0">
        <img
          src="/assets/commerce-intelligence-hero.png"
          alt=""
          className="h-full w-full object-cover object-center opacity-[0.82]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(7,16,15,0.96)_0%,rgba(7,16,15,0.84)_34%,rgba(7,16,15,0.42)_74%,rgba(7,16,15,0.28)_100%)]" />
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-porcelain to-transparent" />
      </div>

      <div className="relative mx-auto grid min-h-[680px] max-w-7xl content-center px-4 pb-24 pt-20 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="mb-5 inline-flex items-center gap-2 rounded-lg border border-white/[0.15] bg-white/[0.08] px-3 py-2 text-sm font-medium text-white/80">
            <span className="h-2 w-2 rounded-full bg-signal" />
            {localized(site.eyebrow, language)}
          </p>
          <h1 className="max-w-4xl text-balance text-5xl font-semibold leading-[1.04] tracking-normal sm:text-6xl lg:text-7xl">
            {localized(site.title, language)}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-white/74 sm:text-xl">
            {localized(site.lead, language)}
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            <a
              href={site.key === 'group' ? '#overview' : '#focus'}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-bold text-ink shadow-quiet transition hover:bg-signal"
            >
              {localized(site.cta, language)}
              <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/[0.18] bg-white/[0.08] px-5 py-3 text-sm font-bold text-white transition hover:bg-white hover:text-ink"
            >
              {secondaryLabel}
              <Mail className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function GroupHome({
  site,
  language,
  copy
}: {
  site: SiteContent;
  language: 'en' | 'zh';
  copy: Record<string, string>;
}) {
  return (
    <>
      <section id="overview" className="section-pad bg-porcelain">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow={copy.overview}
            title={language === 'zh' ? '一家从长期商业运营中成长起来的科技集团。' : 'A group built from commerce operations, not technology hype.'}
            body={
              language === 'zh'
                ? 'MINI MALL GROUP 将商户服务、跨境商业、社区分销与智能自动化放在同一个长期运营体系中。集团故事不是从单一 AI 产品开始，而是从商户服务、交易流程、社群运营和服务能力持续演进而来。'
                : 'MINI MALL GROUP brings together operating businesses that support merchants, cross-border commerce, community distribution and intelligent automation. The group story starts with merchant service, transaction workflows, community operations and service capability.'
            }
          />
          <FeatureGrid features={site.features} language={language} />
        </div>
      </section>

      <section id="business-units" className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow={copy.units}
            title={language === 'zh' ? '三条业务线，一个持续演进的集团平台。' : 'Three businesses, one evolving operating platform.'}
            body={
              language === 'zh'
                ? 'Enring 提供商业和结算基础，MINI MALL 扩展私域社区商业，Sentribee AI 将运营智能延伸到行业运营场景。'
                : 'Enring provides the foundation, MINI MALL scales community commerce, and Sentribee AI extends operational intelligence into industry operations.'
            }
          />
          <div className="grid gap-4 lg:grid-cols-3">
            {groupUnits.map((unit) => {
              const Icon = unit.icon;
              return (
                <article key={unit.key} className="unit-card group">
                  <div className="flex items-start justify-between gap-4">
                    <div className="grid h-12 w-12 place-items-center rounded-lg bg-ink text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="rounded-lg bg-porcelain px-3 py-1 text-xs font-semibold text-moss">{unit.tone}</span>
                  </div>
                  <h3 className="mt-8 text-2xl font-semibold">{unit.name}</h3>
                  <p className="mt-2 text-sm font-semibold text-moss">{localized(unit.title, language)}</p>
                  <p className="mt-4 leading-7 text-ink/70">{localized(unit.body, language)}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {unit.keywords.map((keyword) => (
                      <span key={keyword} className="rounded-lg border border-ink/10 px-3 py-1.5 text-xs font-semibold text-ink/60">
                        {keyword}
                      </span>
                    ))}
                  </div>
                  <a
                    href={unit.href}
                    target={unit.external ? '_blank' : undefined}
                    rel={unit.external ? 'noreferrer' : undefined}
                    className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-ink"
                  >
                    {copy.learn}
                    {unit.external ? <ExternalLink className="h-4 w-4" /> : <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />}
                  </a>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <FlywheelSection language={language} copy={copy} />

      <section id="investor-highlights" className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow={copy.highlights}
            title={language === 'zh' ? '建立在已运营业务之上的长期平台。' : 'A long-term platform built on operating businesses.'}
            body={
              language === 'zh'
                ? '集团围绕商户服务、交易基础设施、私域分发、运营数据与应用型 AI 构建长期价值，而不是依赖短期概念包装。'
                : 'The group is designed around merchant services, transaction infrastructure, private-domain distribution, operational data and applied AI, giving investors a practical view of how the platform can compound over time.'
            }
          />
          <FeatureGrid features={site.proof} language={language} compact />
        </div>
      </section>

      <Vision language={language} copy={copy} />
      <Contact language={language} />
    </>
  );
}

function BusinessHome({
  site,
  language,
  copy
}: {
  site: SiteContent;
  language: 'en' | 'zh';
  copy: Record<string, string>;
}) {
  const siblingUnits = groupUnits.filter((unit) => unit.key !== (site.key === 'minimall' ? 'minimall' : 'enring'));

  return (
    <>
      <section id="focus" className="section-pad bg-porcelain">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
          <div>
            <SectionHeading eyebrow={copy.focus} title={localized(site.focus, language)} body={localized(site.lead, language)} />
            <a
              href="https://mmallgroup.com/"
              className="inline-flex items-center gap-2 rounded-lg border border-ink/[0.12] px-4 py-3 text-sm font-bold text-ink transition hover:bg-ink hover:text-white"
            >
              {copy.groupHome}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <FeatureGrid features={site.features} language={language} />
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow={copy.highlights}
            title={language === 'zh' ? '可执行、可扩展、可连接集团平台。' : 'Executable, scalable and connected to the group platform.'}
            body={
              language === 'zh'
                ? '业务站点聚焦当前能力，同时保持与集团长期 AI 和商业网络叙事的一致性。'
                : 'Each business site focuses on current capability while remaining aligned with the group’s long-term commerce and AI narrative.'
            }
          />
          <FeatureGrid features={site.proof} language={language} compact />
        </div>
      </section>

      <FlywheelSection language={language} copy={copy} />

      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow={copy.builtIn}
            title={language === 'zh' ? '连接集团其他能力。' : 'Connected to the wider group.'}
            body={
              language === 'zh'
                ? '业务单元独立表达，也与集团主站和 Sentribee AI 形成清晰链接。'
                : 'The business unit stands on its own while linking clearly to the group site and Sentribee AI.'
            }
          />
          <div className="grid gap-4 md:grid-cols-2">
            {siblingUnits.map((unit) => {
              const Icon = unit.icon;
              return (
                <a
                  key={unit.key}
                  href={unit.href}
                  target={unit.external ? '_blank' : undefined}
                  rel={unit.external ? 'noreferrer' : undefined}
                  className="unit-card group block"
                >
                  <div className="flex items-center gap-4">
                    <span className="grid h-11 w-11 place-items-center rounded-lg bg-ink text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block text-lg font-semibold">{unit.name}</span>
                      <span className="text-sm text-ink/60">{localized(unit.title, language)}</span>
                    </span>
                  </div>
                  <p className="mt-5 leading-7 text-ink/70">{localized(unit.body, language)}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold">
                    {copy.visit}
                    {unit.external ? <ExternalLink className="h-4 w-4" /> : <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      <Contact language={language} />
    </>
  );
}

function SectionHeading({
  eyebrow,
  title,
  body,
  dark = false
}: {
  eyebrow: string;
  title: string;
  body: string;
  dark?: boolean;
}) {
  return (
    <div className="mb-10 max-w-3xl">
      <p className={`mb-3 text-sm font-bold uppercase ${dark ? 'text-signal' : 'text-moss/70'}`}>{eyebrow}</p>
      <h2 className={`text-balance text-3xl font-semibold leading-tight sm:text-4xl ${dark ? 'text-white' : 'text-ink'}`}>{title}</h2>
      <p className={`mt-4 text-lg leading-8 ${dark ? 'text-white/70' : 'text-ink/70'}`}>{body}</p>
    </div>
  );
}

function FeatureGrid({
  features,
  language,
  compact
}: {
  features: Feature[];
  language: 'en' | 'zh';
  compact?: boolean;
}) {
  return (
    <div className={`grid gap-4 ${compact ? 'md:grid-cols-3' : 'md:grid-cols-3 lg:grid-cols-3'}`}>
      {features.map((feature) => {
        const Icon = feature.icon;
        return (
          <article key={localized(feature.title, language)} className="feature-card">
            <div className="grid h-11 w-11 place-items-center rounded-lg bg-moss text-white">
              <Icon className="h-5 w-5" />
            </div>
            <h3 className="mt-6 text-xl font-semibold">{localized(feature.title, language)}</h3>
            <p className="mt-3 leading-7 text-ink/70">{localized(feature.body, language)}</p>
          </article>
        );
      })}
    </div>
  );
}

function FlywheelSection({ language, copy }: { language: 'en' | 'zh'; copy: Record<string, string> }) {
  return (
    <section id="growth-flywheel" className="section-pad bg-ink text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={copy.flywheel}
          title={language === 'zh' ? '从商业基础设施到智能运营的增长飞轮。' : 'A business flywheel from commerce infrastructure to intelligent operations.'}
          body={
            language === 'zh'
              ? '商业基础设施支撑商户网络，商户网络推动社区商业，社区商业沉淀运营数据，运营智能再把数据转化为自动化、效率和新的增长机会。'
              : 'Commerce infrastructure supports merchant networks. Merchant networks enable community commerce. Community commerce creates operational data, and operational intelligence turns that data into automation, efficiency and new growth opportunities.'
          }
          dark
        />
        <div className="grid gap-3 md:grid-cols-7">
          {flywheel.map((item, index) => (
            <div key={item.en} className="flywheel-step">
              <span className="text-xs font-bold text-signal">{String(index + 1).padStart(2, '0')}</span>
              <span className="mt-3 block text-sm font-semibold leading-6">{localized(item, language)}</span>
              {index < flywheel.length - 1 && <ChevronRight className="mt-4 hidden h-4 w-4 text-white/40 md:block" />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Vision({ language, copy }: { language: 'en' | 'zh'; copy: Record<string, string> }) {
  const industries =
    language === 'zh'
      ? ['建筑', '零售', '物流', '物业', '农业', '工业运营']
      : ['Construction', 'Retail', 'Logistics', 'Property', 'Agriculture', 'Industrial Operations'];

  return (
    <section id="future-vision" className="section-pad bg-porcelain">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
        <div>
          <SectionHeading
            eyebrow={copy.vision}
            title={language === 'zh' ? '把智能带入现场与行业运营。' : 'Bringing intelligence into field and industry operations.'}
            body={
              language === 'zh'
                ? 'Sentribee AI 是集团面向未来的运营智能平台，结合边缘 AI、计算机视觉、传感器与自动化能力，服务建筑、零售、物流、物业、农业与工业运营等多种场景。现有网站保持独立运营，集团主站只负责清晰链接与战略叙事。'
                : 'Sentribee AI is the group’s future-facing operational intelligence platform, combining edge AI, computer vision, sensors and automation for construction, retail, logistics, property, agriculture and industrial operations. The existing site remains independent; this group site links to it as part of the strategic narrative.'
            }
          />
          <div className="mb-7 flex flex-wrap gap-2">
            {industries.map((industry) => (
              <span key={industry} className="rounded-lg border border-ink/10 bg-white px-3 py-1.5 text-xs font-semibold text-ink/60">
                {industry}
              </span>
            ))}
          </div>
          <a
            href="https://sentribee.ai/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-ink px-5 py-3 text-sm font-bold text-white transition hover:bg-moss"
          >
            Sentribee AI
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
        <div className="rounded-lg border border-ink/10 bg-white p-4 shadow-quiet">
          <img
            src="/assets/commerce-intelligence-hero.png"
            alt=""
            className="aspect-[4/3] w-full rounded-lg object-cover object-right"
          />
        </div>
      </div>
    </section>
  );
}

function Contact({ language }: { language: 'en' | 'zh' }) {
  return (
    <section id="contact" className="bg-white px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto grid max-w-7xl gap-8 rounded-lg bg-ink p-8 text-white sm:p-10 lg:grid-cols-[1fr_auto] lg:items-center">
        <div>
          <p className="text-sm font-bold uppercase text-signal">{language === 'zh' ? '联系' : 'Contact'}</p>
          <h2 className="mt-3 max-w-3xl text-3xl font-semibold leading-tight sm:text-4xl">{localized(contact.title, language)}</h2>
          <p className="mt-4 max-w-2xl leading-7 text-white/70">{localized(contact.body, language)}</p>
        </div>
        <a
          href={`mailto:${contact.email}`}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-bold text-ink transition hover:bg-signal"
        >
          <Mail className="h-4 w-4" />
          {contact.email}
        </a>
      </div>
    </section>
  );
}

function Footer({ language }: { language: 'en' | 'zh' }) {
  return (
    <footer className="border-t border-ink/10 bg-porcelain px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 text-sm text-ink/60 md:flex-row md:items-center md:justify-between">
        <p>© {new Date().getFullYear()} Mini Mall Group Ltd</p>
        <div className="flex flex-wrap gap-4">
          {siteLinks.map((link) => (
            <a
              key={link.key}
              href={link.href}
              target={link.external ? '_blank' : undefined}
              rel={link.external ? 'noreferrer' : undefined}
              className="inline-flex items-center gap-1 hover:text-ink"
            >
              {link.label}
              {link.external && <ExternalLink className="h-3.5 w-3.5" />}
            </a>
          ))}
        </div>
        <p>{language === 'zh' ? '英文 / 简体中文' : 'English / Simplified Chinese'}</p>
      </div>
    </footer>
  );
}

function MiniMallFooter({ language }: { language: 'en' | 'zh' }) {
  return (
    <footer className="border-t border-[#f0e5e8] bg-white px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 text-sm text-[#6d767d] md:flex-row md:items-center md:justify-between">
        <div>
          <p className="font-semibold tracking-normal text-[#1e2022]">MINI MALL</p>
          <p className="mt-1">{language === 'zh' ? 'MINI MALL GROUP 旗下社群商业网络业务。' : 'Part of MINI MALL GROUP.'}</p>
          <p className="mt-1">© 2017-{new Date().getFullYear()} MINI MALL LTD</p>
        </div>
        <div className="flex flex-wrap gap-4">
          <a href="https://mmallgroup.com/" className="hover:text-[#d4145a]">MINI MALL GROUP</a>
          <a href="https://enring.co.nz/" className="hover:text-[#d4145a]">Enring</a>
          <a href="https://sentribee.ai/" target="_blank" rel="noreferrer" className="hover:text-[#d4145a]">Sentribee AI</a>
        </div>
      </div>
    </footer>
  );
}

function DeepMiniMallSite({
  language,
  setLanguage,
  copy
}: {
  site: SiteContent;
  language: 'en' | 'zh';
  setLanguage: (language: 'en' | 'zh') => void;
  copy: Record<string, string>;
}) {
  const page = getBusinessPage('minimall');
  const nav = [
    ['home', language === 'zh' ? '主页' : 'Home'],
    ['private-domain', language === 'zh' ? '私域分销' : 'Private Domain'],
    ['merchants', language === 'zh' ? '商户增长' : 'Merchants'],
    ['agents', language === 'zh' ? '代理网络' : 'Agents'],
    ['wechat', language === 'zh' ? '微信生态' : 'WeChat'],
    ['fulfillment', language === 'zh' ? '履约闭环' : 'Fulfillment'],
    ['creative-hub', 'Creative Hub']
  ] as const;

  const creativeModules = [
    {
      slug: 'content-creation',
      name: 'Content Creation',
      icon: Video,
      title: language === 'zh' ? '为创作者、社群与数字活动打造更有吸引力的内容' : 'Engaging content for creators, communities and digital campaigns',
      body:
        language === 'zh'
          ? '我们为多个平台制作适合传播、互动与活动推广的数字内容。'
          : 'We create engaging digital content for creators, communities and online campaigns across multiple platforms.',
      points: ['Promotional Videos', 'Short-form Videos', 'Community Content', 'Campaign Materials', 'Visual Storytelling'],
      image: '/assets/minimall-banner-home.svg'
    },
    {
      slug: 'live-streaming-support',
      name: 'Live Streaming Support',
      icon: Radio,
      title: language === 'zh' ? '为直播活动提供完整的多媒体视觉支持' : 'Multimedia support that makes live streams more engaging',
      body:
        language === 'zh'
          ? '我们为直播活动提供多媒体支持，提升观众参与度与内容呈现质量。'
          : 'We provide multimedia support for live streaming activities to improve audience engagement and content quality.',
      points: ['Live Event Graphics', 'Stream Overlays', 'Opening & Closing Animations', 'Live Broadcast Assets', 'Streaming Visual Packages'],
      image: '/assets/igggo-hero.jpg'
    },
    {
      slug: 'graphic-motion-design',
      name: 'Graphic & Motion Design',
      icon: Palette,
      title: language === 'zh' ? '用平面与动态设计强化品牌表达' : 'Graphic and motion design that strengthens brand expression',
      body:
        language === 'zh'
          ? '从社交媒体图形到动态内容，我们为品牌和活动建立统一、清晰的视觉表达。'
          : 'From social graphics to animated content, we create clear and consistent visual expression for brands and campaigns.',
      points: ['Motion Graphics', 'Animated Content', 'Banner Design', 'Social Media Graphics', 'Brand Assets'],
      image: '/assets/minimall-banner-merchants.svg'
    },
    {
      slug: 'digital-marketing',
      name: 'Digital Marketing',
      icon: Megaphone,
      title: language === 'zh' ? '让营销内容更适合平台传播与用户转化' : 'Marketing content designed for reach, relevance and conversion',
      body:
        language === 'zh'
          ? '我们围绕社交媒体、线上广告和活动需求制作并优化营销内容。'
          : 'We create and optimise marketing content for social media, online advertising and campaign needs.',
      points: ['Campaign Visuals', 'Social Media Content', 'Online Advertising Creatives', 'Content Optimisation', 'Marketing Materials'],
      image: '/assets/minimall-banner-private-domain.svg'
    },
    {
      slug: 'creator-resources',
      name: 'Creator Resources',
      icon: BookOpen,
      title: language === 'zh' ? '帮助创作者更高效地制作与发布内容' : 'Practical resources for creating and publishing with confidence',
      body:
        language === 'zh'
          ? '面向创作者提供工具、教程、媒体规范、品牌资源与平台动态。'
          : 'Resources for creators including tools, tutorials, media guidelines, branding support and platform updates.',
      points: ['Creator Toolkit', 'Tutorials', 'Media Guidelines', 'Branding Resources', 'Platform Updates'],
      image: '/assets/minimall-banner-agents.svg'
    },
    {
      slug: 'creative-portfolio',
      name: 'Portfolio',
      icon: Images,
      title: language === 'zh' ? '查看内容、设计与社群活动的创意示意' : 'A visual selection of content, design and community work',
      body:
        language === 'zh'
          ? '通过活动、视频、设计与社群项目示意，了解 Creative Hub 的视觉方向与服务范围。'
          : 'Explore indicative campaign, video, design and community work across the Creative Hub service range.',
      points: ['Campaign Showcase', 'Video Gallery', 'Design Gallery', 'Community Events', 'Success Stories'],
      image: '/assets/minimall-banner-wechat.svg'
    }
  ];

  const creativeHubHero = {
    eyebrow: 'MINI MALL Creative Hub',
    title: language === 'zh' ? '连接创作者、社群与数字活动的创意内容中心' : 'Creative content for creators, communities and digital campaigns',
    body:
      language === 'zh'
        ? 'Creative Hub 提供内容制作、直播支持、平面与动态设计、数字营销及创作者资源，帮助品牌与社群在多个平台持续表达和互动。'
        : 'Creative Hub brings together content production, live streaming support, graphic and motion design, digital marketing and practical creator resources.',
    image: '/assets/minimall-banner-home.svg'
  };

  const homeHero = {
    eyebrow: language === 'zh' ? '跨境社群商业网络' : 'Cross-Border Community Commerce Network',
    title: language === 'zh' ? '让海外品牌进入中国私域商业网络' : 'Help overseas brands enter China’s private commerce network',
      body:
        language === 'zh'
        ? 'MINI MALL 帮助海外品牌通过微信生态、代理网络、返现激励与跨境履约能力进入中国市场，建立可分销、可复购、可沉淀的增长体系。'
        : 'MINI MALL helps overseas brands enter China through the WeChat ecosystem, agent networks, cashback incentives and cross-border fulfillment, building repeatable growth through distribution and repurchase.',
    image: '/assets/minimall-china-welcome-hero.png'
  };

  const detail: Record<string, { eyebrow: string; title: string; body: string; points: string[]; image: string }> = {
    'private-domain': {
      eyebrow: language === 'zh' ? '私域分销系统' : 'Private-Domain System',
      title: language === 'zh' ? '把信任关系变成可运营的分销网络' : 'Turn trusted relationships into an operating distribution network',
      body:
        language === 'zh'
          ? 'MINI MALL 围绕私域分销展开，让微信社群、代理关系、推荐来源和订单归属形成清晰的增长路径，帮助品牌看见商品如何被推荐、成交并带来复购。'
          : 'MINI MALL connects WeChat communities, agent relationships, referral sources and order attribution into a clear growth path, helping brands understand how products are recommended, purchased and reordered.',
      image: '/assets/minimall-banner-private-domain.svg',
      points:
        language === 'zh'
          ? ['微信社群、朋友圈、公众号、小程序统一承接', '推荐关系和订单归属可追踪', '返现、佣金、活动奖励自动核算', '价格权益按代理等级和活动策略控制']
          : ['WeChat groups, Moments, official accounts and mini-programs', 'Trackable referrals and order attribution', 'Automated cashback, commission and campaign rewards', 'Price rights by agent level and campaign policy']
    },
    merchants: {
      eyebrow: language === 'zh' ? '商户增长' : 'Merchant Growth',
      title: language === 'zh' ? '让海外品牌用更低门槛进入中国私域市场' : 'Help overseas brands enter China’s private-domain market with lower friction',
      body:
        language === 'zh'
          ? '商户可以获得商品上架、活动配置、代理授权、内容素材、库存同步、订单履约和数据反馈，让进入中国私域市场的过程更清晰。'
          : 'Merchants receive product listing, campaign setup, agent authorization, media assets, inventory sync, order operations and data feedback, making China private-domain entry clearer.',
      image: '/assets/minimall-banner-merchants.svg',
      points:
        language === 'zh'
          ? ['商品和活动快速进入微信销售场景', '代理素材、推广链接、社群活动统一管理', '保税仓、清关、支付和物流状态串联', '复购和分销数据反哺品牌运营']
          : ['Fast product and campaign entry into WeChat commerce', 'Unified agent assets, links and community campaigns', 'Bonded warehouse, clearance, payment and logistics connected', 'Repurchase and distribution data feeds brand operations']
    },
    agents: {
      eyebrow: language === 'zh' ? '代理与团长网络' : 'Agent Network',
      title: language === 'zh' ? '让代理、推广者和团长有明确权益与反馈' : 'Give agents, promoters and community leaders clear rights and feedback',
      body:
        language === 'zh'
          ? '平台把代理等级、推广任务、订单归属、返现佣金和社群复购联系起来，支持团长裂变和跨层级协作。'
          : 'The platform connects agent levels, promotion tasks, order attribution, cashback, commission and community repurchase.',
      image: '/assets/minimall-banner-agents.svg',
      points:
        language === 'zh'
          ? ['代理等级和价格权益配置', '推广素材和商品链接发放', '订单归属、佣金和返现反馈', '团长社群活动和复购提醒']
          : ['Agent levels and price rights', 'Promotional assets and product links', 'Order attribution, commission and cashback feedback', 'Community campaigns and reorder reminders']
    },
    wechat: {
      eyebrow: language === 'zh' ? '微信生态成交' : 'WeChat Commerce',
      title: language === 'zh' ? '在用户熟悉的微信场景里完成发现、咨询和购买' : 'Complete discovery, consultation and purchase inside familiar WeChat contexts',
      body:
        language === 'zh'
          ? 'MINI MALL 将公众号、小程序、社群、朋友圈和客服触点组织成成交路径，尽量减少用户跳转，让信任推荐直接转化为订单。'
          : 'MINI MALL organizes official accounts, mini-programs, groups, Moments and service touchpoints into a conversion path.',
      image: '/assets/minimall-banner-wechat.svg',
      points:
        language === 'zh'
          ? ['公众号内容种草', '小程序承接下单和支付', '社群和朋友圈推动信任转化', '客服触点处理咨询和售后']
          : ['Official-account content for discovery', 'Mini-program ordering and payment', 'Groups and Moments for trusted conversion', 'Service touchpoints for consultation and support']
    },
    fulfillment: {
      eyebrow: language === 'zh' ? '跨境履约闭环' : 'Cross-Border Fulfillment',
      title: language === 'zh' ? '私域分销的后端必须可靠' : 'Private distribution needs reliable back-end operations',
      body:
        language === 'zh'
          ? '平台连接保税仓、订单触发清关、跨境支付、外币结算和物流通知，让海外品牌不必先成为清关和仓配专家。'
          : 'The platform connects bonded warehousing, order-triggered clearance, cross-border payment, settlement and logistics notification.',
      image: '/assets/minimall-banner-fulfillment.svg',
      points:
        language === 'zh'
          ? ['货物到仓后再按订单触发清关', '支付和结算由集团能力支撑', '物流状态回传给用户和代理', '履约结果进入复购和社群运营']
          : ['Clearance triggered by actual orders', 'Payment and settlement supported by group capability', 'Logistics status returned to users and agents', 'Fulfillment data feeds repurchase and community operations']
    }
  };

  const creativeModule = creativeModules.find((module) => module.slug === page);
  const isCreativeHub = page === 'creative-hub';
  const isCreativePage = isCreativeHub || Boolean(creativeModule);
  const active = creativeModule
    ? {
        eyebrow: 'Creative Hub',
        title: creativeModule.title,
        body: creativeModule.body,
        points: creativeModule.points,
        image: creativeModule.image
      }
    : detail[page];
  const hero = isCreativeHub ? creativeHubHero : active ?? homeHero;
  const portfolioItems = [
    ['Campaign Showcase', '/assets/minimall-banner-merchants.svg'],
    ['Video Gallery', '/assets/igggo-hero.jpg'],
    ['Design Gallery', '/assets/minimall-banner-wechat.svg'],
    ['Community Events', '/assets/minimall-china-welcome-hero.png'],
    ['Success Stories', '/assets/minimall-banner-agents.svg']
  ];
  const flow = language === 'zh'
    ? [
        ['品牌入驻', '梳理商品、价格、素材和目标客群。'],
        ['代理分发', '通过代理与团长把商品带入信任社群。'],
        ['微信成交', '在公众号、小程序、社群和客服触点完成转化。'],
        ['返现佣金', '让推广者和消费者获得清晰的激励反馈。'],
        ['清关发货', '连接支付、清关、仓配和物流通知。'],
        ['复购运营', '把成交用户沉淀为可持续服务的私域客户。']
      ]
    : [
        ['Brand onboarding', 'Prepare products, pricing, media assets and target audiences.'],
        ['Agent distribution', 'Bring products into trusted communities through agents and leaders.'],
        ['WeChat conversion', 'Convert through official accounts, mini-programs, groups and service touchpoints.'],
        ['Rewards', 'Give promoters and customers clear incentive feedback.'],
        ['Clearance', 'Connect payment, clearance, warehousing and logistics updates.'],
        ['Repurchase', 'Turn buyers into private-domain customers for ongoing service.']
      ];
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="minimall-page min-h-screen bg-white text-[#1e2022]">
      <header className="minimall-header">
        <a href={businessHref('minimall', 'home')} aria-label="MINI MALL" className="minimall-brand-link">
          <MiniMallLogo />
        </a>
        <nav className="hidden items-center gap-4 text-[13px] lg:flex xl:gap-6 xl:text-sm">
          {nav.map(([slug, label]) => (
            <a key={slug} href={businessHref('minimall', slug)} className="text-[#1e2022] transition hover:text-[#d4145a]">
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <PlainLanguageSwitch language={language} setLanguage={setLanguage} label={copy.language} mode="dark" />
          <a href="#contact" className="hidden rounded px-5 py-3 text-sm font-semibold text-white md:inline-flex minimall-cta">
            {copy.contact}
          </a>
          <button
            type="button"
            className="business-menu-button minimall-menu-button lg:hidden"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Toggle navigation"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>
      {menuOpen && (
        <div className="business-mobile-menu minimall-mobile-menu lg:hidden">
          {nav.map(([slug, label]) => (
            <a key={slug} href={businessHref('minimall', slug)} onClick={() => setMenuOpen(false)}>
              {label}
              <ChevronRight className="h-4 w-4" />
            </a>
          ))}
          <a href="#contact" onClick={() => setMenuOpen(false)}>
            {copy.contact}
            <Mail className="h-4 w-4" />
          </a>
        </div>
      )}

      <main>
        <section className="minimall-hero">
          <div className="mx-auto grid min-h-[760px] max-w-7xl items-center gap-12 px-4 pt-28 sm:px-6 lg:grid-cols-[1fr_0.88fr] lg:px-8">
            <div className="minimall-photo">
              <img src={hero.image} alt="" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#d4145a]">
                {hero.eyebrow}
              </p>
              <h1 className="mt-5 max-w-3xl break-words text-4xl font-light leading-[1.14] tracking-normal sm:text-5xl lg:text-6xl">
                {hero.title}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-[#6d767d]">
                {hero.body}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {isCreativePage ? (
                  <>
                    <a href={isCreativeHub ? '#creative-services' : businessHref('minimall', 'creative-hub')} className="rounded px-6 py-3 text-sm font-semibold text-white minimall-cta">
                      {isCreativeHub ? (language === 'zh' ? '浏览创意服务' : 'Explore Creative Services') : (language === 'zh' ? '返回 Creative Hub' : 'Back to Creative Hub')}
                    </a>
                    <a href={isCreativeHub ? '#portfolio' : '#contact'} className="rounded border border-[#d4145a]/25 px-6 py-3 text-sm font-semibold text-[#d4145a]">
                      {isCreativeHub ? (language === 'zh' ? '查看作品示意' : 'View Portfolio') : (language === 'zh' ? '咨询创意项目' : 'Discuss a Project')}
                    </a>
                  </>
                ) : (
                  <>
                    <a href={businessHref('minimall', 'private-domain')} className="rounded px-6 py-3 text-sm font-semibold text-white minimall-cta">
                      {language === 'zh' ? '了解业务模式' : 'Explore the Model'}
                    </a>
                    <a href={businessHref('minimall', 'merchants')} className="rounded border border-[#d4145a]/25 px-6 py-3 text-sm font-semibold text-[#d4145a]">
                      {language === 'zh' ? '品牌合作咨询' : 'Partner with MINI MALL'}
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {active && (
          <section className="minimall-section bg-[#fafafa]">
            <MiniMallTitle eyebrow={active.eyebrow} title={active.title} body={active.body} />
            <div className="mx-auto mt-10 grid max-w-6xl gap-4 px-4 sm:px-6 md:grid-cols-2 lg:px-8">
              {active.points.map((point) => (
                <div key={point} className="minimall-role">
                  <h3>{point}</h3>
                  <p>
                    {creativeModule
                      ? (language === 'zh' ? '作为 Creative Hub 服务的一部分，可根据品牌、创作者或活动需求组合应用。' : 'Available as part of Creative Hub and adaptable to brand, creator and campaign requirements.')
                      : (language === 'zh' ? '帮助品牌在微信生态、代理网络和订单履约之间建立清晰连接，让每一次推荐和成交都能服务后续增长。' : 'Helps brands connect WeChat channels, agent networks and order operations so each referral and purchase can support future growth.')}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {isCreativePage && (
          <>
            <section id="creative-services" className="minimall-section bg-white">
              <MiniMallTitle
                eyebrow="Creative Hub"
                title={language === 'zh' ? '六个创意服务模块，覆盖从内容到传播的完整需求。' : 'Six creative service areas spanning content, design and digital reach.'}
                body={language === 'zh' ? '每个模块均可独立进入，查看具体服务内容并选择适合项目的创意支持。' : 'Explore each service area independently and identify the right creative support for your project.'}
              />
              <div className="mx-auto mt-12 grid max-w-7xl gap-5 px-4 sm:px-6 md:grid-cols-2 lg:grid-cols-3 lg:px-8">
                {creativeModules.map((module) => {
                  const Icon = module.icon;
                  return (
                    <a
                      key={module.slug}
                      href={businessHref('minimall', module.slug)}
                      className="group min-h-[320px] rounded border border-[#f0e5e8] bg-white p-7 shadow-[0_20px_60px_rgba(30,32,34,0.07)] transition hover:-translate-y-1 hover:border-[#d4145a]/35"
                    >
                      <div className="grid h-12 w-12 place-items-center rounded bg-[#fff1f5] text-[#d4145a]">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h2 className="mt-6 text-2xl font-semibold text-[#1e2022]">{module.name}</h2>
                      <p className="mt-4 leading-7 text-[#6d767d]">{module.body}</p>
                      <span className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#d4145a]">
                        {language === 'zh' ? '查看服务内容' : 'Explore service'}
                        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                      </span>
                    </a>
                  );
                })}
              </div>
            </section>

            <section id="portfolio" className="minimall-section bg-[#f7f8fa]">
              <MiniMallTitle
                eyebrow="Portfolio"
                title={language === 'zh' ? '创意方向与项目形式示意。' : 'Indicative creative directions and project formats.'}
                body={language === 'zh' ? '以下图片用于展示 Creative Hub 可覆盖的内容与视觉形式，具体项目将根据品牌、平台和活动目标定制。' : 'These images illustrate the types of content and visual work Creative Hub can support. Final work is tailored to each brand, platform and campaign objective.'}
              />
              <div className="mx-auto mt-12 grid max-w-7xl gap-5 px-4 sm:px-6 md:grid-cols-2 lg:grid-cols-3 lg:px-8">
                {portfolioItems.map(([title, image], index) => (
                  <article key={title} className={`${index === 0 ? 'md:col-span-2' : ''} overflow-hidden rounded bg-white shadow-[0_18px_50px_rgba(30,32,34,0.08)]`}>
                    <img src={image} alt="" className="aspect-[16/10] w-full object-cover" />
                    <div className="p-5">
                      <h2 className="text-lg font-semibold text-[#1e2022]">{title}</h2>
                      <p className="mt-2 text-sm leading-6 text-[#6d767d]">{language === 'zh' ? 'Creative Hub 创意服务示意' : 'Creative Hub service concept'}</p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </>
        )}

        {!isCreativePage && (
          <>
        <section className="minimall-section bg-white">
          <MiniMallTitle
            eyebrow={language === 'zh' ? '分销系统' : 'Distribution System'}
            title={language === 'zh' ? '从私域触达到复购增长的完整服务链路。' : 'A complete service path from private-domain reach to repurchase growth.'}
            body={
              language === 'zh'
                ? '品牌、代理、团长和消费者在同一套关系网络里协作，让订单、佣金、返现、清关和复购形成清晰的商业闭环。'
                : 'Brands, agents, community leaders and customers work in one relationship network where orders, rewards, clearance and repurchase form a clear commerce loop.'
            }
          />
          <div className="mx-auto mt-12 grid max-w-7xl gap-5 px-4 sm:px-6 md:grid-cols-4 lg:px-8">
            {[
              [language === 'zh' ? '私域社群分销' : 'Private communities', language === 'zh' ? '将社群、朋友圈、公众号和小程序组织成成交路径。' : 'Organize groups, Moments, official accounts and mini-programs into conversion paths.'],
              [language === 'zh' ? '代理等级体系' : 'Agent levels', language === 'zh' ? '按身份、等级和活动配置不同价格权益。' : 'Configure price rights by identity, level and campaign.'],
              [language === 'zh' ? '返现佣金核算' : 'Reward calculation', language === 'zh' ? '自动识别推荐关系并计算返现、佣金和奖励。' : 'Identify referral relationships and calculate cashback, commission and incentives.'],
              [language === 'zh' ? '跨境履约连接' : 'Fulfillment connection', language === 'zh' ? '连接保税仓、清关、支付、结算和物流通知。' : 'Connect bonded warehouse, clearance, payment, settlement and logistics notification.']
            ].map(([title, body]) => (
              <article key={title} className="minimall-card">
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="mt-4 leading-7 text-[#6d767d]">{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="minimall-section bg-[#f7f8fa]">
          <MiniMallTitle
            eyebrow={language === 'zh' ? '运营闭环' : 'Operating Loop'}
            title={language === 'zh' ? '从品牌入驻到社群复购的完整链路。' : 'A complete path from brand onboarding to community repurchase.'}
            body={language === 'zh' ? 'MINI MALL 将私域分发、微信成交、跨境履约和复购服务连接起来，让海外品牌可以更清楚地进入并经营中国私域市场。' : 'MINI MALL connects private-domain distribution, WeChat conversion, cross-border fulfillment and repurchase service so overseas brands can enter and serve China’s private-domain market with greater clarity.'}
          />
          <div className="mx-auto mt-12 grid max-w-7xl gap-4 px-4 sm:px-6 lg:grid-cols-6 lg:px-8">
            {flow.map(([step, description], index) => (
              <div key={step} className="minimall-step">
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{step}</h3>
                <p>{description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="minimall-section bg-[#d4145a] text-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/75">WeChat Private Domain</p>
              <h2 className="mt-4 text-4xl font-semibold leading-tight">
                {language === 'zh' ? '深耕微信生态，连接海外品牌与中国私域消费。' : 'Built for WeChat private-domain commerce between overseas brands and China consumers.'}
              </h2>
              <p className="mt-5 leading-8 text-white/82">
                {language === 'zh'
                  ? 'MINI MALL 帮助海外品牌把微信生态中的信任推荐转化为持续销售、履约服务和复购关系。'
                  : 'MINI MALL helps overseas brands turn trusted WeChat referrals into sales, fulfillment service and repeat customer relationships.'}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                ['1000+', language === 'zh' ? '跨境社交团购团长经验' : 'Community leader experience'],
                ['5000+', language === 'zh' ? '海外直播带货场次经验' : 'Livestream commerce sessions'],
                ['20M+', language === 'zh' ? '集团跨境支付能力支撑' : 'Group payment capability'],
                ['Loop', language === 'zh' ? '分销、成交、履约、复购闭环' : 'Distribution, conversion, fulfillment, repurchase']
              ].map(([value, label]) => (
                <div key={value} className="rounded bg-white/12 p-6">
                  <p className="text-3xl font-semibold">{value}</p>
                  <p className="mt-3 text-sm leading-6 text-white/78">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
          </>
        )}

        <Contact language={language} />
        <MiniMallFooter language={language} />
      </main>
    </div>
  );
}

function DeepEnringSite({
  site,
  language,
  setLanguage,
  copy
}: {
  site: SiteContent;
  language: 'en' | 'zh';
  setLanguage: (language: 'en' | 'zh') => void;
  copy: Record<string, string>;
}) {
  const nav = [
    ['#payment', language === 'zh' ? '支付能力' : 'Payment'],
    ['#wechat-pay', language === 'zh' ? '微信支付' : 'WeChat Pay'],
    ['#merchant-service', language === 'zh' ? '商户服务' : 'Merchant Service'],
    ['#contact', language === 'zh' ? '联系' : 'Contact']
  ] as const;

  const [menuOpen, setMenuOpen] = useState(false);
  const paymentCards = [
    [
      language === 'zh' ? '微信支付受理' : 'WeChat Pay Acceptance',
      language === 'zh'
        ? '帮助新西兰商户接入面向中国消费者熟悉的微信支付收款方式。'
        : 'Enable New Zealand merchants to accept a payment method familiar to Chinese customers.'
    ],
    [
      language === 'zh' ? '商户开通与配置' : 'Merchant Setup',
      language === 'zh'
        ? '支持商户资料、收款场景、门店信息和基础运营流程配置。'
        : 'Support merchant details, payment contexts, store information and operating setup.'
    ],
    [
      language === 'zh' ? '交易与结算支持' : 'Transaction & Settlement Support',
      language === 'zh'
        ? '围绕交易记录、对账、结算相关流程和日常支付问题提供支持。'
        : 'Support transaction records, reconciliation, settlement-related workflows and payment operations.'
    ],
    [
      language === 'zh' ? '跨境消费场景' : 'Cross-Border Commerce',
      language === 'zh'
        ? '服务旅游、零售、餐饮、服务业和跨境消费相关商户场景。'
        : 'Serve tourism, retail, hospitality, service and cross-border consumption contexts.'
    ]
  ];
  const serviceCards = [
    [
      language === 'zh' ? '支付方案咨询' : 'Payment Consultation',
      language === 'zh'
        ? '根据商户行业、客群和收款场景，梳理适合的支付接入路径。'
        : 'Map the right payment setup based on merchant sector, customers and acceptance scenarios.'
    ],
    [
      language === 'zh' ? '商户运营支持' : 'Merchant Operations',
      language === 'zh'
        ? '协助商户处理开通、测试、上线、对账和日常服务问题。'
        : 'Assist merchants through onboarding, testing, launch, reconciliation and daily service questions.'
    ],
    [
      language === 'zh' ? '集团商业连接' : 'Group Commerce Connection',
      language === 'zh'
        ? '与 MINI MALL GROUP 的商户网络、跨境商业和社群商业能力形成连接。'
        : 'Connect with MINI MALL GROUP merchant networks, cross-border commerce and community commerce capabilities.'
    ]
  ];

  return (
    <div className="enring-page min-h-screen bg-white text-[#6f6f6f]">
      <header className="enring-header">
        <a href="/" aria-label="Enring" className="flex items-center gap-3">
          <img src="/assets/enring-logo.png" alt="Enring" className="h-8 w-auto" />
        </a>
        <nav className="hidden items-center gap-7 text-sm lg:flex">
          {nav.map(([href, label]) => (
            <a key={href} href={href} className="text-white transition hover:text-[#8dbdff]">
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <PlainLanguageSwitch language={language} setLanguage={setLanguage} label={copy.language} mode="light" />
          <button
            type="button"
            className="business-menu-button enring-menu-button lg:hidden"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label="Toggle navigation"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>
      {menuOpen && (
        <div className="business-mobile-menu enring-mobile-menu lg:hidden">
          {nav.map(([href, label]) => (
            <a key={href} href={href} onClick={() => setMenuOpen(false)}>
              {label}
              <ChevronRight className="h-4 w-4" />
            </a>
          ))}
          <a href="#contact" onClick={() => setMenuOpen(false)}>
            {copy.contact}
            <Mail className="h-4 w-4" />
          </a>
        </div>
      )}

      <main>
        <section className="enring-hero">
          <div className="mx-auto grid min-h-[760px] max-w-7xl items-center gap-12 px-4 pt-24 text-white sm:px-6 lg:grid-cols-[1fr_0.82fr] lg:px-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#8dbdff]">
                {language === 'zh' ? '新西兰商户支付能力' : 'New Zealand Merchant Payment Capability'}
              </p>
              <h1 className="mt-5 max-w-5xl text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
                {language === 'zh' ? '面向商户的支付受理与微信支付服务' : 'Merchant payment acceptance and WeChat Pay service'}
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-white/85">
                {localized(site.lead, language)}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#payment" className="rounded bg-[#3a7cdd] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#2d65b6]">
                  {language === 'zh' ? '查看支付能力' : 'View payment capability'}
                </a>
                <a href="#wechat-pay" className="rounded border border-white/30 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white hover:text-[#1f2b38]">
                  {language === 'zh' ? '了解微信支付' : 'WeChat Pay background'}
                </a>
              </div>
            </div>
            <div className="enring-hero-panel">
              <img src="/assets/enring-hero.png" alt="" className="enring-hero-panel-image" />
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8dbdff]">Payment Focus</p>
              <h2 className="mt-4 text-3xl font-semibold text-white">
                {language === 'zh' ? '新西兰较早提供微信支付服务的本地服务商之一' : 'Among New Zealand’s earliest local WeChat Pay service providers'}
              </h2>
              <div className="mt-8 grid gap-3">
                {[
                  language === 'zh' ? '微信支付商户接入' : 'WeChat Pay merchant enablement',
                  language === 'zh' ? '交易、对账与结算相关支持' : 'Transaction, reconciliation and settlement support',
                  language === 'zh' ? '本地与跨境消费场景服务' : 'Local and cross-border commerce service'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded bg-white/10 p-4 text-sm font-semibold text-white/88">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#8dbdff]" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="payment" className="enring-section bg-white">
          <EnringTitle
            blue={language === 'zh' ? '支付' : 'Payment'}
            rest={language === 'zh' ? '能力' : 'Capability'}
            body={
              language === 'zh'
                ? 'Enring 的核心是帮助商户建立可靠的收款能力，特别是面向中国消费者、新西兰本地商户和跨境消费场景的微信支付与支付运营支持。'
                : 'Enring focuses on reliable merchant acceptance, especially WeChat Pay and payment operations for Chinese customers, New Zealand merchants and cross-border commerce scenarios.'
            }
          />
          <div className="mx-auto mt-12 grid max-w-7xl gap-6 px-4 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
            {paymentCards.map(([title, body]) => (
              <article key={title} className="feature-card">
                <div className="grid h-11 w-11 place-items-center rounded bg-[#3a7cdd] text-white">
                  <WalletCards className="h-5 w-5" />
                </div>
                <h3 className="mt-6 text-xl font-semibold text-[#4f5963]">{title}</h3>
                <p className="mt-3 leading-7 text-[#6f6f6f]">{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="wechat-pay" className="enring-section bg-[#f7f7f9]">
          <EnringTitle
            blue="WeChat"
            rest="Pay"
            body={
              language === 'zh'
                ? 'Enring 是新西兰较早提供微信支付商户服务的本地服务商之一，长期围绕商户开通、收款场景、交易支持和支付运营提供服务。'
                : 'Enring was among the earliest local New Zealand service providers supporting WeChat Pay for merchants, with long-running work around onboarding, acceptance scenarios, transaction support and payment operations.'
            }
          />
          <div className="mx-auto mt-12 grid max-w-6xl gap-4 px-4 sm:px-6 md:grid-cols-3 lg:px-8">
            {[
              language === 'zh' ? '较早进入新西兰微信支付服务市场' : 'Early entrant in New Zealand WeChat Pay service',
              language === 'zh' ? '服务本地商户与中国消费者支付习惯' : 'Connect local merchants with Chinese consumer payment habits',
              language === 'zh' ? '围绕收款、对账、结算相关流程提供持续支持' : 'Support acceptance, reconciliation and settlement-related workflows'
            ].map((point) => (
              <div key={point} className="enring-market">
                <span className="bg-[#3a7cdd]" />
                {point}
              </div>
            ))}
          </div>
        </section>

        <section id="merchant-service" className="enring-section bg-white">
          <EnringTitle
            blue={language === 'zh' ? '商户' : 'Merchant'}
            rest={language === 'zh' ? '服务' : 'Service'}
            body={
              language === 'zh'
                ? '产品和服务保持简化：以支付能力为核心，配套商户开通、支付运营和集团商业连接，不把页面做成复杂产品目录。'
                : 'The service offering is intentionally simple: payment capability first, supported by merchant onboarding, payment operations and group commerce connections.'
            }
          />
          <div className="mx-auto mt-12 grid max-w-6xl gap-6 px-4 sm:px-6 md:grid-cols-3 lg:px-8">
            {serviceCards.map(([title, body]) => (
              <article key={title} className="feature-card">
                <h3 className="text-xl font-semibold text-[#4f5963]">{title}</h3>
                <p className="mt-4 leading-7 text-[#6f6f6f]">{body}</p>
              </article>
            ))}
          </div>
        </section>

        <Contact language={language} />
        <EnringFooter language={language} />
      </main>
    </div>
  );
}

function EnringFooter({ language }: { language: 'en' | 'zh' }) {
  return (
    <footer className="border-t border-[#e4e9ef] bg-[#f7f7f9] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 text-sm text-[#6f6f6f] md:flex-row md:items-center md:justify-between">
        <div>
          <p className="font-semibold tracking-normal text-[#4f5963]">Enring</p>
          <p className="mt-1">
            {language === 'zh' ? 'FSP 代码：FSP1006359' : 'FSP code: FSP1006359'}
          </p>
          <p className="mt-1">© 2014-{new Date().getFullYear()} ENRING NEW ZEALAND LTD</p>
        </div>
        <div className="flex flex-wrap gap-4">
          <a href="https://mmallgroup.com/" className="hover:text-[#3a7cdd]">MINI MALL GROUP</a>
          <a href="https://mmall.co.nz/" className="hover:text-[#3a7cdd]">MINI MALL</a>
          <a href="https://sentribee.ai/" target="_blank" rel="noreferrer" className="hover:text-[#3a7cdd]">Sentribee AI</a>
        </div>
      </div>
    </footer>
  );
}

export default App;
